<template>
  <div class="form-container">
    <h1>鳥獣被害一覧</h1>
    <div class="search-toggle" id="search-toggle">
      <button @click="toggleDropdown" class="dropbtn">
        検索条件を表示/非表示
        <span v-if="!isDropdownVisible">▼</span>
        <span v-else>▲</span>
      </button>
      <div class="search-condition" id="search-condition" v-show="isDropdownVisible">
        <table class="search-table">
          <tr>
            <td><label for="showAll">全て表示</label></td>
            <td><label>:</label></td>
            <td><input type="checkbox" v-model="showAll" id="showAll" /></td>
          </tr>
          <tr>
            <td><label class="required-label">地域</label></td>
            <td><label>:</label></td>
            <td>
              <select v-model="selectedRegion" :disabled="disableRegion">
                <option value="" disabled>地域を選択してください</option>
                <option 
                  v-for="region in regions" 
                  :key="region.region_id" 
                  :value="region.region_id">
                  {{ region.region_nm }}
                </option>
              </select>
            </td>
          </tr>
          <tr>
            <td><label>獣種</label></td>
            <td><label>:</label></td>
            <td>
              <div class="checkbox-group">
                <div v-for="(chunk, index) in chunkedAnimalConditions" :key="index" class="checkbox-row">
                  <label v-for="condition in chunk" :key="condition" class="checkbox-item">
                    <input type="checkbox" v-model="selectedConditions" :value="condition" />
                    {{ condition }}
                  </label>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td><label for="userIdSearch">利用者ID</label></td>
            <td><label>:</label></td>
            <td>
              <input type="text" v-model="idSearch" maxlength="8" id="userIdSearch" />
            </td>
          </tr>
          <tr>
            <td><label for="regionSelect">確認場所</label></td>
            <td><label>:</label></td>
            <td>
              <select v-model="selectedCity" id="citySelect">
                <option v-for="municipalities in municipalities" :key="municipalities" :value="municipalities">{{ municipalities }}</option>
              </select>
            </td>
          </tr>
          <tr>
            <td><label for="dateFrom">日付</label></td>
            <td><label>:</label></td>
            <td>
              <input type="date" v-model="dateFrom" id="dateFrom" />
              ～ 
              <input type="date" v-model="dateTo" id="dateTo" />
            </td>
          </tr>
        </table>
        <div class="search-button-container">
          <button @click="filterItems" class="search-button">検索</button>
        </div>
      </div>
    </div>
    <div class="action-buttons">
      <button @click="displayButton" class="display-button">表示</button>
      <button @click="deleteButton" class="delete-button">削除</button>
    </div>
    <div class="list-table-container" id="app">
      <div>
        <table>
          <thead>
            <tr>
              <th>受付番号</th>
              <th>利用者ID</th>
              <th>氏名</th>
              <th>種別</th>
              <th>獣種</th>
              <th>確認場所</th>
              <th>日時</th>
              <th>選択</th>
            </tr>
          </thead>
          <tbody id="table-body">
            <tr 
              v-for="row in filteredItems" 
              :key="row.no"
              :class="{'highlight-row': (this.section !== '0000') && (row.directAdminAgency !== this.directAdminAgency)}"
            >
              <td>{{ row.no }}</td>
              <td>{{ row.id }}</td>
              <td>{{ row.nameKanji }}</td>
              <td>{{ typeMapping[row.type] }}</td>
              <td>{{ animalTypeMapping[row.animalType] }}</td>
              <td>{{ row.municipalities }}</td>
              <td>{{ formatDate(row.damageDate) }}</td>
              <td>
                <input 
                  type="checkbox" 
                  v-model="selectedItems" 
                  :value="row"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import "@/assets/styles/Wildlife-list-common.css";
import axios from "axios";

export default {
  name: "WildlifeDamageList",

  data() {
    return {
      showAll: false,
      items: [],
      regions: [],
      section: '',
      filteredItems: [],
      selectedItems: [],
      directAdminAgency: '',
      disableRegion: true,
      isDropdownVisible: false,
      selectedRegion: "",
      selectedConditions: [],
      idSearch: '',
      selectedCity: '',
      municipalities: [],
      dateFrom: '',
      dateTo: '',
      animalConditions: [
        "イノシシ", "ニホンジカ", "クマ", "サル", "タヌキ", 
        "アナグマ", "ハクビシン", "アライグマ", 
        "ヌートリア", "鳥類", "その他"
      ],
      typeMapping: {
        "1": "被害",
        "2": "捕獲",
        "3": "目撃",
        "4": "痕跡",
        "5": "わな",
        "9": "その他"
      },
      animalTypeMapping: {
        "01": "イノシシ",
        "02": "ニホンジカ",
        "03": "クマ",
        "04": "サル",
        "05": "タヌキ",
        "06": "アナグマ",
        "07": "ハクビシン",
        "08": "アライグマ",
        "09": "ヌートリア",
        "10": "鳥類",
        "11": "その他"
      }
    };
  },

  computed: {
    chunkedAnimalConditions() {
      const chunkSize = 5;
      return this.animalConditions.reduce((chunks, condition, index) => {
        const chunkIndex = Math.floor(index / chunkSize);
        if (!chunks[chunkIndex]) chunks[chunkIndex] = [];
        chunks[chunkIndex].push(condition);
        return chunks;
      }, []);
    },
  },

  created() {
    const query = this.$route.query;
    this.selectedConditions = query.selectedConditions ? JSON.parse(query.selectedConditions) : [];
    this.idSearch = query.idSearch || '';
    this.selectedCity = query.selectedCity || '';
    // 現在のローカルタイムを取得
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 現在の月（0始まりではないように調整）
    // 今年度の4月を計算
    const fiscalYear = month >= 4 ? year : year - 1; // 4月以降ならその年、3月までなら前年
    const fiscalMonth = '04'; // 4月
    const fiscalDay = '01'; // 1日
    // 日付と時刻を設定
    this.dateFrom = query.dateFrom || `${fiscalYear}-${fiscalMonth}-${fiscalDay}`;
    this.dateTo = query.dateTo || '';
    this.showAll = query.showAll === 'true' ? true : false;
    this.isDropdownVisible = query.isDropdownVisible === 'true';
    this.section = localStorage.getItem("section");
    this.directAdminAgency = localStorage.getItem("directAdminAgency");
    this.getRegionDetails();

    this.searchItems().then(() => {
      this.filterItems();
    });
  },

  methods: {
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
      this.$router.replace({
          name: 'WildlifeDamageList',
          query: {
              ...this.$route.query,
              isDropdownVisible: this.isDropdownVisible.toString()
          },
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return '不明'; // nullやundefinedの場合は「不明」を返す

      // UTC日時から日本時間に変換
      const date = new Date(dateStr);

      // JST (UTC + 9時間)に変換
      date.setHours(date.getHours() + 9);

      // フォーマット: yyyy年MM月dd日 hh:mm
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // 月を2桁に
      const day = String(date.getDate()).padStart(2, '0'); // 日を2桁に
      const hours = String(date.getHours()).padStart(2, '0'); // 時間を2桁に
      const minutes = String(date.getMinutes()).padStart(2, '0'); // 分を2桁に

      return `${year}年${month}月${day}日 ${hours}:${minutes}`;
    },

    async searchItems() {
      const token = localStorage.getItem("jwtToken");
      const userId = localStorage.getItem("userId");
      const section = localStorage.getItem("section");
      if (!token) {
        alert("ログインしていません。再度ログインしてください。");
        return;
      }

      try {
        // リクエストボディに id を含める
        const requestData = { id: userId, section: section };

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/wildlifeDamage/getList`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 && response.data.message === "検索成功") {
          // 新しいトークンがレスポンスヘッダーに含まれている場合
          const newToken = response.headers.get('Authorization');
          if (newToken) {
              localStorage.setItem('jwtToken', newToken.replace('Bearer ', ''));
          }
          // レスポンスの data を items と filteredItems に格納
          this.items = response.data.data;
          console.log(this.items);
          this.filteredItems = [...this.items];
          // 確認場所のリストを取得（種別フィルタリングを適用）
          this.municipalities = ["未選択",...new Set(
              this.items
                .filter((item) => item.type !== "5" && item.type !== "6") // 種別フィルタリング
                .map((item) => {
                  // municipalitiesがオブジェクトの場合、適切なキーを選択
                  return item.municipalities;
                })
              ),
            ];    
          } else {
          console.warn(`Unexpected status code: ${response.status}`);
        }
      } catch (error) {
        this.items = [];
        this.filteredItems = [];
      }
    },

    filterItems() {
      const dateFormat = /^\d{4}-\d{2}-\d{2}$/;

      if (
        (this.dateFrom && !dateFormat.test(this.dateFrom)) ||
        (this.dateTo && !dateFormat.test(this.dateTo)) ||
        (this.dateFrom && this.dateTo && this.dateFrom > this.dateTo)
      ) {
        alert("有効な日付を指定してください。");
        return;
      }

      this.filteredItems = this.items.filter((item) => {

        // 地域フィルタリング（selectedRegion が空の場合はすべて許可）
        const isRegionMatched = !this.selectedRegion || item.region_Id === this.selectedRegion;

        // 獣種の条件一致を animalTypeMapping を用いて処理
        const isConditionMatched =
          this.selectedConditions.length === 0 ||
          this.selectedConditions.some(
            (condition) => this.animalTypeMapping[item.animalType] === condition
          );

        const isIdMatched = this.idSearch === '' || item.id.toString().includes(this.idSearch);

        const isCityMatched =
          this.selectedCity === '未選択' ||
          this.selectedCity === '' ||
          (item.municipalities && (
            typeof item.municipalities === 'string'
              ? item.municipalities === this.selectedCity
              : item.municipalities.prefecture === this.selectedCity
          ));

        let isDateMatched = false;

        // 被害一覧では日付はdamageDateのみを対象とする
        const itemDateStr = item.damageDate;

        if (itemDateStr) {
          const itemDate = new Date(itemDateStr);
          const fromDate = this.dateFrom ? new Date(this.dateFrom) : null;
          const toDate = this.dateTo ? new Date(this.dateTo) : null;

          if (fromDate && toDate) {
            toDate.setHours(23, 59, 59, 999);
            isDateMatched = itemDate >= fromDate && itemDate <= toDate;
          } else if (fromDate) {
            isDateMatched = itemDate >= fromDate;
          } else if (toDate) {
            toDate.setHours(23, 59, 59, 999);
            isDateMatched = itemDate <= toDate;
          } else {
            isDateMatched = true; // 両方未指定の場合はすべて許容
          }
        }

        // 種別フィルタリング（被害、捕獲、目撃、痕跡、その他を表示）
        const isTypeMatched = ['1', '2', '3', '4', '9'].includes(item.type);

        // 削除フラグの条件追加
        const isDeleteFlagMatched = item.deleteFlg !== '1';

        // わなと柵を除外
        const isTrapOrFenceExcluded = !['5', '6'].includes(item.type);

        return (
          isRegionMatched &&
          isConditionMatched &&
          isIdMatched &&
          isCityMatched &&
          isDateMatched &&
          isTypeMatched &&
          isDeleteFlagMatched &&
          isTrapOrFenceExcluded
        );
      });

      // 検索条件をURLに反映
      this.$router.replace({
        name: 'WildlifeDamageList',
        query: {
          selectedConditions: JSON.stringify(this.selectedConditions),
          idSearch: this.idSearch,
          selectedCity: this.selectedCity,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
          showAll: this.showAll.toString(),
          isDropdownVisible: this.isDropdownVisible.toString(),
        },
      });
    },

    displayButton() {
      if (!this.selectedItems.length) {
        alert("項目を選択してください。");
        return;
      }
      console.log(this.searchItems)
      this.$router.push({ 
        name: 'WildlifeDamageListDetail', 
        query: { data: encodeURIComponent(JSON.stringify(this.selectedItems)) } 
      });
    },

    async getRegionDetails() {
      const token = localStorage.getItem("jwtToken");
      const userId = localStorage.getItem("userId");
      const section = localStorage.getItem("section");
      const regionId = localStorage.getItem("regionId");

      if (!token) {
        alert("ログインしていません。再度ログインしてください。");
        return;
      }

      if (!userId) {
        alert("ログイン情報が見つかりません。再度ログインしてください。");
        return;
      }

      try {
        // リクエストボディに id を含める
        const requestData = { id: userId };

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/regionSearch/getList`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200) {
          // システム管理者のみ利用可能
          if (section !== '0000') {
            this.disableRegion = true;
            this.regions = response.data.data.filter(org =>
              org.region_id === regionId
            );
            this.selectedRegion = this.regions[0].region_id
          } else {
            this.disableRegion = false;
            this.regions = response.data.data;
          }
          // 新しいトークンがレスポンスヘッダーに含まれている場合
          const newToken = response.headers.get('Authorization');
          if (newToken) {
              localStorage.setItem('jwtToken', newToken.replace('Bearer ', ''));
          }
        } else {
          alert(`データの取得に失敗しました。ステータスコード: ${response.status}`);
        }
      } catch (error) {
        alert(`タイムアウトです。再ログインしてください。`);
      }
    },
    
    async deleteButton() {
      const token = localStorage.getItem("jwtToken");
      const section = localStorage.getItem("section");
      if (!token) {
        alert("ログインしていません。再度ログインしてください。");
        return;
      }
      
      if (!this.selectedItems.length) {
        alert("項目を選択してください。");
        return;
      }

      if (this.selectedItems.length > 1) {
        alert("削除時は複数選択できません。");
        return;
      }

      if ((section !== '0000') && (this.selectedItems[0].directAdminAgency !== this.directAdminAgency)) {
        alert("所属行政機関のデータは削除できません。");
        return;
      }

      const confirmDelete = confirm("本当に削除しますか。");
      if (!confirmDelete) {
        return; // キャンセルの場合は処理を終了
      }

      try {
        const selectedItem = this.selectedItems[0]; // 選択された項目（1つのみ）
        const deleteItem = {
          primaryKey: {
            region_Id: selectedItem.region_Id,
            no: selectedItem.no,
            id: selectedItem.id
          }
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/wildlifeDamageMap/delete`,
          deleteItem,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // ステータスコード200の場合（送信成功）
        if (response.status === 200) {
          // 新しいトークンがレスポンスヘッダーに含まれている場合
          const newToken = response.headers.get('Authorization');
          if (newToken) {
              localStorage.setItem('jwtToken', newToken.replace('Bearer ', ''));
          }
          alert("データが削除されました。");
        } else if (response.status === 202) {
          alert(response.data.message);
        }
      } catch (error) {
        // エラーハンドリング
        if (error.response && error.response.status === 400) {
          alert(error.response.data.message || "リクエストにエラーがあります。");
        } else if (error.response && error.response.status === 401) {
          alert("認証エラー: ログイン情報を確認してください。");
        } else if (error.response && error.response.status === 500) {
          alert("サーバーエラーが発生しました。しばらくしてからお試しください。");
        } else {
          alert(`タイムアウトです。再ログインしてください。`);
        }
      }

      this.searchItems().then(() => {
        this.filterItems();
      });
      // 選択ボックスをクリア
      this.selectedItems = [];
    }
  }
};
</script>

<style scoped>
.highlight-row {
  color: red;
}
</style>
